import React from 'react';
import { motion } from 'framer-motion';
import { ModeProps } from '../../types';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
export function HowItWorks({
  mode
}: ModeProps) {
  const isBusiness = mode === 'business';
  if (isBusiness) {
    const steps = [{
      title: 'Connect',
      desc: 'Integrate via API or Dashboard'
    }, {
      title: 'Access Rails',
      desc: 'Local banking & crypto networks'
    }, {
      title: 'Move Funds',
      desc: 'Convert & transfer instantly'
    }, {
      title: 'Settle',
      desc: 'Finalize globally in seconds'
    }];
    return <section className="py-24 bg-slate-900 text-white overflow-hidden" data-id="element-275">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
          <div className="mb-12 sm:mb-16 text-center sm:text-left" data-id="element-277">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-id="element-278">How it works</h2>
            <p className="text-slate-400 text-base sm:text-lg" data-id="element-279">
              Streamlined flow for complex operations.
            </p>
          </div>

          <div className="relative" data-id="element-280">
            {/* Connecting line */}
            <div className="absolute top-8 left-0 w-full h-0.5 bg-slate-800 hidden md:block" data-id="element-281" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12" data-id="element-282">
              {steps.map((step, i) => <motion.div key={step.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="relative z-10 text-center md:text-left flex flex-col items-center md:items-start" data-id="element-283">
                  <div className="w-16 h-16 rounded-full bg-slate-800 border-4 border-slate-900 flex items-center justify-center text-xl font-bold mb-6" data-id="element-284">
                    {i + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-2" data-id="element-285">{step.title}</h3>
                  <p className="text-slate-400 text-sm" data-id="element-286">{step.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>;
  }
  // Individual Mode (Vertical)
  const steps = [{
    title: 'Create account',
    desc: 'Verify your identity in minutes.'
  }, {
    title: 'Add funds',
    desc: 'Deposit crypto or fiat via bank transfer.'
  }, {
    title: 'Use globally',
    desc: 'Send, spend, or save without borders.'
  }];
  return <section className="py-24 bg-white" data-id="element-287">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-289">
          <div data-id="element-290">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center lg:text-left" data-id="element-291">
              Get started in minutes
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-12 text-center lg:text-left" data-id="element-292">
              No paperwork, no waiting. Just download the app and go.
            </p>

            <div className="space-y-12" data-id="element-293">
              {steps.map((step, i) => <div key={step.title} className="flex gap-6" data-id="element-294">
                  <div className="flex flex-col items-center" data-id="element-295">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm" data-id="element-296">
                      {i + 1}
                    </div>
                    {i !== steps.length - 1 && <div className="w-0.5 h-full bg-slate-100 my-2" data-id="element-297" />}
                  </div>
                  <div className="pb-8" data-id="element-298">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-299">
                      {step.title}
                    </h3>
                    <p className="text-slate-600" data-id="element-300">{step.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="relative h-[600px] bg-slate-50 rounded-2xl border border-slate-100 p-8 flex items-center justify-center overflow-hidden" data-id="element-301">
            {/* Abstract App UI representation */}
            <div className="w-64 h-[500px] bg-white rounded-[3rem] shadow-2xl border-8 border-slate-900 relative overflow-hidden" data-id="element-302">
              <div className="absolute top-0 left-0 right-0 h-32 bg-slate-50 border-b border-slate-100 p-6 flex flex-col justify-end" data-id="element-303">
                <div className="w-12 h-12 rounded-full bg-slate-200 mb-4" data-id="element-304" />
                <div className="w-24 h-4 bg-slate-200 rounded-full" data-id="element-305" />
              </div>
              <div className="p-6 space-y-4" data-id="element-306">
                <div className="w-full h-20 bg-blue-50 rounded-xl" data-id="element-307" />
                <div className="w-full h-20 bg-slate-50 rounded-xl" data-id="element-308" />
                <div className="w-full h-20 bg-slate-50 rounded-xl" data-id="element-309" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}